import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Shield, 
  Eye, 
  Mail, 
  Lock,
  Database,
  Sparkles,
  ArrowRight,
  CheckCircle,
  Info,
  Cookie,
  Share2
} from "lucide-react";
import { getPrivacyPolicySEO, validateSEOData, generateKeywords } from "@/services/seoService";

import { isGoogleAdsConfigured, openAdsPreferences } from "@/services/googleAdsService";

const PrivacyPolicy = () => {
  const [totalNavHeight, setTotalNavHeight] = useState(112); // Default navbar height

  // Listen for navigation height updates from NavBar
  useEffect(() => {
    const handleNavHeightChange = (event: CustomEvent) => {
      setTotalNavHeight(event.detail.height);
    };

    // Listen for height updates from NavBar
    window.addEventListener('navHeightUpdate', handleNavHeightChange as EventListener);

    return () => {
      window.removeEventListener('navHeightUpdate', handleNavHeightChange as EventListener);
    };
  }, []);

  const sections = [
    {
      id: "introduction",
      title: "1. Introduction",
      icon: "👋",
      content: `JamPack'd Care Packages ("we," "us," "our") is committed to protecting your privacy. This Privacy Policy explains what personal information we collect, how we use and share it, and the choices you have. By using our website or services, you agree to this policy.`
    },
    {
      id: "information-collection",
      title: "2. Information We Collect",
      icon: "📊",
      subsections: [
        {
          title: "2.1 Registration and Account Information",
          content: `• Name, email address, password (via Firebase Auth)
• Google OAuth data (name, email, Google user ID)
• Newsletter opt-in preference
• Customer lifetime metrics (order count, total spent)`
        },
        {
          title: "2.2 Checkout and Order Information",
          content: `• Shipping and billing addresses (via Stripe AddressElement)
• Phone number
• Payment intent identifiers (Stripe)
• Order history and purchase behavior`
        },
        {
          title: "2.3 Browsing and Usage Data",
          content: `• Cart contents stored in localStorage (keys: jamaicaCart, jamaicaShippingOption)
• Product views and interactions
• Referral tracking via a 30-day "jampackd_ref" cookie
• Banner dismissals stored in sessionStorage`
        }
      ]
    },
    {
      id: "information-usage",
      title: "3. How We Use Your Information",
      icon: "🎯",
      content: `We use your data to:

• Process and fulfill orders
• Communicate order status, shipping updates, newsletters, and promotions (with your consent)
• Prevent fraud and secure our platform
• Improve our products and services`
    },
    {
      id: "cookies-tracking",
      title: "4. Cookies and Tracking Technologies",
      icon: "🍪",
      subsections: [
        {
          title: "4.1 First-Party Cookies and Storage",
          content: `• jampackd_ref: 30-day affiliate/referral cookie
• Firebase Auth tokens and authentication storage keep you signed in
• localStorage and sessionStorage for cart persistence and UI preferences`
        },
        {
          title: "4.2 Third-Party Trackers",
          content: `We use PostHog for product usage analytics and error reporting. Stripe handles payment interactions.

When Google Ads measurement is enabled, its tag loads only after you allow it in our advertising measurement preferences. It uses cookies and ad click identifiers (gclid, gbraid or wbraid) to connect ad visits with completed purchases. We send a verified transaction reference, the amount charged and its currency. We do not send your name, email, address or payment details in the conversion event, and enhanced conversions and ad personalization are disabled.

We remember your measurement preference in localStorage. After acceptance, sessionStorage keeps ad click identifiers during navigation and records transaction references to help prevent repeat conversion events. Declining prevents our Google tag from loading on subsequent visits. Withdrawing consent stops further purchase events and requests removal of Google Ads measurement cookies. The choice does not affect checkout or our other tools. You can change it using the button on this page.`
        }
      ]
    },
    {
      id: "data-storage-security",
      title: "5. Data Storage and Security",
      icon: "🔒",
      content: `• Storage: Firebase Firestore for structured data; Firebase Storage for images
• Transmission: HTTPS/TLS encryption for all data in transit
• At Rest: Google Cloud's default encryption for Firestore and Storage
• Access Controls: Firebase security rules enforce user isolation; Stripe webhook signatures are verified
• Password Security: Strong password requirements and Firebase Auth protections`
    },
    {
      id: "data-sharing",
      title: "6. Data Sharing",
      icon: "🤝",
      content: `We share your personal information only as needed to provide our services:

• Stripe: payment processing (card data, billing address, email, order details)
• EasyPost: shipping address, package details, HS codes, customs information
• Ship24: tracking numbers and delivery status updates
• Resend: transactional and marketing emails (email addresses only)
• OpenAI: product data enrichment (non-personal product information)
• Vercel: front-end hosting (deployment logs and minimal analytics)
• Firebase/Google Cloud: application data storage and authentication
• PostHog: product usage analytics and error reporting
• Google Ads: advertising measurement when you allow it, as described above

We do not sell or rent your personal information to third parties for marketing purposes.`
    },
    {
      id: "third-party-links",
      title: "7. Third-Party Links",
      icon: "🔗",
      content: `Our site may contain links to third-party websites. This Privacy Policy does not apply to those sites. We encourage you to review their privacy policies before providing any personal information.`
    },
    {
      id: "rights-choices",
      title: "8. Your Rights and Choices",
      icon: "⚖️",
      content: `• Access and Correction: Via your account settings you can update your name, email, password, and addresses
• Unsubscribe: Use our one-click unsubscribe page to stop marketing emails
• Opt-Out of Marketing: Email support@jampackd.store with "Unsubscribe" in the subject line
• Data Deletion: Though no self-service deletion exists, you may request account and data deletion by contacting us at support@jampackd.store
• Data Export: You may request a copy of your data by emailing support@jampackd.store`
    },
    {
      id: "age-eligibility",
      title: "9. Age and Eligibility",
      icon: "🔞",
      content: `You must be at least 18 years old to use our services. We rely on your self-certification at registration. We do not knowingly collect personal information from anyone under 18.`
    },
    {
      id: "policy-changes",
      title: "10. Changes to This Policy",
      icon: "📝",
      content: `We may update this Privacy Policy at any time by posting the revised version on our website. Your continued use after changes indicates acceptance. We recommend reviewing this page periodically.`
    },
    {
      id: "contact",
      title: "11. Contact Us",
      icon: "📧",
      content: `For questions or privacy requests, please contact:

JamPack'd Care Packages
35A Upper Waterloo Road
Kingston 8, Saint Andrew
Jamaica
Email: support@jampackd.store`
    }
  ];

  // Generate SEO data for Privacy Policy page
  const seoData = validateSEOData(getPrivacyPolicySEO());

  return (
    <>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <meta name="author" content={seoData.author} />
        
        {/* Open Graph Tags for Social Sharing */}
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:image" content={seoData.image} />
        <meta property="og:url" content={seoData.url} />
        <meta property="og:type" content={seoData.type} />
        <meta property="og:site_name" content={seoData.siteName} />
        <meta property="og:locale" content={seoData.locale} />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />
        <meta name="twitter:image" content={seoData.image} />
        
        {/* Additional SEO Tags */}
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={seoData.url} />
      </Helmet>
      
      <div 
        className="flex flex-col"
        style={{ minHeight: `calc(100vh - ${totalNavHeight}px)` }}
      >
      <main className="flex-grow relative bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Floating Geometric Shapes */}
          <div className="absolute top-20 left-20 w-16 h-16 bg-gradient-to-br from-jamaica-green/10 to-jamaica-green/5 rounded-2xl transform rotate-45 animate-pulse delay-300"></div>
          <div className="absolute top-40 right-32 w-12 h-12 bg-jamaica-yellow/15 rounded-full animate-bounce delay-500"></div>
          <div className="absolute bottom-32 left-1/4 w-20 h-20 bg-gradient-to-br from-jamaica-yellow/8 to-jamaica-green/8 rounded-3xl transform rotate-12 animate-ping delay-700"></div>
          <div className="absolute bottom-20 right-20 w-14 h-14 bg-jamaica-green/12 rounded-lg transform -rotate-12 animate-pulse delay-900"></div>
          
          {/* Triangle Decorative Elements */}
          <div className="absolute top-1/4 right-10 w-0 h-0 border-l-[20px] border-r-[20px] border-b-[35px] border-l-transparent border-r-transparent border-b-jamaica-green/10 animate-pulse delay-1000"></div>
          <div className="absolute bottom-1/3 left-16 w-0 h-0 border-l-[15px] border-r-[15px] border-b-[26px] border-l-transparent border-r-transparent border-b-jamaica-yellow/15 animate-bounce delay-1200"></div>
          
          {/* Grid Pattern */}
          <div className="absolute inset-0 opacity-30">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                linear-gradient(rgba(0, 116, 47, 0.05) 1px, transparent 1px),
                linear-gradient(90deg, rgba(0, 116, 47, 0.05) 1px, transparent 1px)
              `,
              backgroundSize: '50px 50px'
            }}></div>
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {isGoogleAdsConfigured() && (
            <div className="mb-6 text-center">
              <Button variant="outline" onClick={openAdsPreferences}>Advertising measurement preferences</Button>
            </div>
          )}
          {/* Enhanced Hero Section */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-3 mb-4">
              <div className="relative">
                <div className="w-12 h-12 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-2xl flex items-center justify-center shadow-xl transform rotate-6">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <div className="absolute -top-2 -right-2 w-5 h-5 bg-jamaica-yellow rounded-full flex items-center justify-center animate-bounce">
                  <Eye className="w-2.5 h-2.5 text-white" />
                </div>
              </div>
              <div className="bg-gradient-to-r from-jamaica-green/10 to-jamaica-yellow/10 px-4 py-1.5 rounded-2xl border border-jamaica-green/20 shadow-lg">
                <span className="text-sm font-bold text-jamaica-black flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5 text-jamaica-green" />
                  Privacy Protection
                </span>
              </div>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 leading-tight">
              <span className="bg-gradient-to-r from-gray-900 via-jamaica-green to-gray-900 bg-clip-text text-transparent p-1">
                Privacy Policy
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-6">
              We're committed to protecting your privacy and being transparent about how we 
              <span className="text-jamaica-yellow font-semibold"> collect and use your data</span>. 
              Learn about your 
              <span className="text-jamaica-green font-semibold"> rights and choices</span>
            </p>

            {/* Important Notice */}
            <div className="max-w-2xl mx-auto">
              <div className="relative backdrop-blur-sm bg-amber-50/80 border-2 border-amber-200 rounded-2xl shadow-lg p-4 hover:shadow-xl transition-all duration-500">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center mt-0.5">
                    <Info className="w-4 h-4 text-white" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-amber-800 mb-1">Effective Date</p>
                    <p className="text-sm text-amber-700">
                      This Privacy Policy is effective as of July 24, 2025. 
                      By using our services, you acknowledge that you have read and understand this policy.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
        
        {/* Privacy Policy Content */}
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
          >
            {sections.map((section, index) => (
              <motion.div
                key={section.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className="relative bg-white/80 backdrop-blur-sm border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
                  {/* Background decoration for each card */}
                  <div className="absolute inset-0 bg-gradient-to-r from-jamaica-green/3 via-transparent to-jamaica-yellow/3"></div>
                  
                  <CardContent className="relative p-6">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
                        <span className="text-xl">{section.icon}</span>
                      </div>
                      <div className="flex-grow">
                        <h2 className="text-xl font-bold text-gray-900 mb-2">{section.title}</h2>
                        {section.content && (
                          <div className="prose prose-gray max-w-none">
                            <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                              {section.content}
                            </p>
                          </div>
                        )}
                        
                        {section.subsections && (
                          <div className="space-y-4 mt-4">
                            {section.subsections.map((subsection, subIndex) => (
                              <div key={subIndex} className="pl-4 border-l-2 border-jamaica-green/20">
                                <h3 className="font-semibold text-gray-900 mb-2">{subsection.title}</h3>
                                <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                                  {subsection.content}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
          
          {/* Enhanced Contact Support Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-12"
          >
            <Card className="relative bg-gradient-to-br from-jamaica-green/5 to-jamaica-yellow/5 border-0 shadow-xl backdrop-blur-sm overflow-hidden">
              {/* Background decoration */}
              <div className="absolute inset-0 bg-gradient-to-r from-jamaica-green/5 via-transparent to-jamaica-yellow/5"></div>
              <div className="absolute top-0 right-0 w-32 h-32 bg-jamaica-yellow/10 rounded-full -translate-y-16 translate-x-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-jamaica-green/10 rounded-full translate-y-12 -translate-x-12"></div>
              
              <CardContent className="relative p-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-2xl mb-6 shadow-lg">
                  <Mail className="h-8 w-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Privacy Questions?</h2>
                <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                  If you have any questions about this Privacy Policy or how we handle your personal information, 
                  our team is here to help. Contact us for data requests, privacy concerns, or policy clarifications.
                </p>
                <div className="flex flex-col gap-4 items-center justify-center">
                  <Button asChild className="bg-gradient-to-r from-jamaica-green to-jamaica-green/90 text-white hover:from-jamaica-green/90 hover:to-jamaica-green shadow-lg hover:shadow-xl transition-all duration-300 px-6 py-3 rounded-xl">
                    <a href="mailto:support@jampackd.store" className="flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      Contact Support
                      <ArrowRight className="h-4 w-4" />
                    </a>
                  </Button>
                  <div className="text-sm">
                    {/* Mobile chips */}
                    <div className="grid grid-cols-1 gap-2 px-4 sm:hidden">
                      <div className="flex items-center gap-2 justify-center rounded-full border border-green-200 bg-green-50/60 text-gray-700 px-3 py-2">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span className="font-medium">Data requests welcome</span>
                      </div>
                      <div className="flex items-center gap-2 justify-center rounded-full border border-green-200 bg-green-50/60 text-gray-700 px-3 py-2">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span className="font-medium">Privacy-focused support</span>
                      </div>
                    </div>
                    {/* Desktop original layout */}
                    <div className="hidden sm:flex items-center gap-6 text-gray-500">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span>Data requests welcome</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span>Privacy-focused support</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </main>
    </div>
    </>
  );
};

export default PrivacyPolicy;